import { SKINS, type SkinId, getSkin } from '../config/skins';

const STORAGE_KEY = 'spliffo:wallet:v1';

export interface WalletState {
  coins: number;
  ownedSkins: SkinId[];
  selectedSkin: SkinId;
}

export type PurchaseResult =
  | { ok: true; newBalance: number }
  | { ok: false; reason: 'already-owned' | 'insufficient-funds' | 'unknown-skin' };

const ALL_IDS: readonly SkinId[] = SKINS.map((s) => s.id);

function defaultState(): WalletState {
  const builtIns = SKINS.filter((s) => s.builtIn).map((s) => s.id);
  return {
    coins: 0,
    ownedSkins: builtIns,
    selectedSkin: builtIns[0] ?? 'ai',
  };
}

function sanitize(raw: Partial<WalletState> | null | undefined): WalletState {
  const base = defaultState();
  if (!raw) return base;
  const coins = Number.isFinite(raw.coins) ? Math.max(0, Math.floor(raw.coins as number)) : 0;
  const owned = Array.isArray(raw.ownedSkins)
    ? (raw.ownedSkins.filter((id) => ALL_IDS.includes(id as SkinId)) as SkinId[])
    : [];
  // Always include built-ins.
  SKINS.filter((s) => s.builtIn).forEach((s) => {
    if (!owned.includes(s.id)) owned.push(s.id);
  });
  const selected =
    typeof raw.selectedSkin === 'string' && owned.includes(raw.selectedSkin as SkinId)
      ? (raw.selectedSkin as SkinId)
      : owned[0] ?? 'ai';
  return { coins, ownedSkins: owned, selectedSkin: selected };
}

/**
 * WalletStore — localStorage-backed persistence for coins, owned skins, and
 * the currently selected opponent skin. Stateless class (all methods static),
 * mirrors HighScoreStore. Reads are cheap enough to do per-access; no in-memory
 * cache means no staleness bugs when two scenes mutate in sequence.
 */
export class WalletStore {
  static read(): WalletState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      return sanitize(JSON.parse(raw));
    } catch {
      return defaultState();
    }
  }

  static write(state: WalletState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore quota / unavailable storage
    }
  }

  static getCoins(): number {
    return WalletStore.read().coins;
  }

  static getSelectedSkin(): SkinId {
    return WalletStore.read().selectedSkin;
  }

  static owns(id: SkinId): boolean {
    return WalletStore.read().ownedSkins.includes(id);
  }

  static addCoins(delta: number): WalletState {
    if (!Number.isFinite(delta) || delta <= 0) return WalletStore.read();
    const state = WalletStore.read();
    state.coins += Math.floor(delta);
    WalletStore.write(state);
    return state;
  }

  /** Purchase a skin and auto-equip it. No-op if already owned or unaffordable. */
  static purchase(id: SkinId): PurchaseResult {
    const skin = SKINS.find((s) => s.id === id);
    if (!skin) return { ok: false, reason: 'unknown-skin' };
    const state = WalletStore.read();
    if (state.ownedSkins.includes(id)) return { ok: false, reason: 'already-owned' };
    if (state.coins < skin.price) return { ok: false, reason: 'insufficient-funds' };
    state.coins -= skin.price;
    state.ownedSkins.push(id);
    state.selectedSkin = id;
    WalletStore.write(state);
    return { ok: true, newBalance: state.coins };
  }

  static selectSkin(id: SkinId): boolean {
    const state = WalletStore.read();
    if (!state.ownedSkins.includes(id)) return false;
    if (state.selectedSkin === id) return true;
    state.selectedSkin = id;
    WalletStore.write(state);
    return true;
  }

  /** Danger: wipes all wallet data. Debug only — no UI hook. */
  static reset(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

export { SKINS, getSkin };
export type { SkinId };
